import { useCallback, useMemo, useState } from "react"
import classNames from "classnames"
import styled from "styled-components"

import { useAppDispatch, useAppSelector } from "store/hooks"
import { setSelectedCustomerDevices } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import DevicePhysicalStatus from "types/enums/DevicePhysicalStatus"
import { ReactComponent as IconEdit } from "assets/svg/icon-edit.svg"

import { convertDevicesToContractorDevicesTableRows } from "utils/convertData"
import { EditThermostatModal } from "modals/EditThermostatModal"
import { MissingModalContractor } from "modals/MissingModalContractor"
import { ReturnFromMissingModalContractor } from "modals/ReturnFromMissingModalContractor"
import { TransferModal } from "modals/TransferModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import TableWrapper from "components/table/TableWrapper"
import { WidgetInventory } from "components/widgets/WidgetInventory"
import { WidgetMonitoring } from "components/widgets/WidgetMonitoring"
import { WidgetStatus } from "components/widgets/WidgetStatus"

import InventoryConfirmationModal from "./InventoryConfirmationModal"
import PreinstalledConfirmationModal from "./PreInstalledConfirmationModal"
import WarrantyConfirmationModal from "./WarrantyConfirmationModal"

import styles from "./index.module.scss"

const NavButtonsWrapper = styled.div`
  width: 100%;
  justify-content: space-between;
`

const RightSideButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`

interface ContractorDevicesPageContentProps {
  devices: ApiTypes.Model.Device[]
  tableHeaderCells: UITypes.TableHeaderCell[]
  isFetching: boolean
  columnsLoaded: boolean
  meta?: ApiTypes.Model.Pagination
  onDevicesActionSuccess: () => void
  onExport: () => void
}

const ContractorDevicesPageContent = ({
  devices,
  tableHeaderCells,
  isFetching,
  meta,
  columnsLoaded,
  onDevicesActionSuccess,
  onExport,
}: ContractorDevicesPageContentProps) => {
  const dispatch = useAppDispatch()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const selectedCustomerDevices = useAppSelector(state => state.tempData.selectedCustomerDevices)

  const isHeaderChecked = useMemo(() => {
    if (!devices.length) return false
    return devices.every(device => selectedCustomerDevices.some(d => d.id === device.id))
  }, [devices, selectedCustomerDevices])
  const [showInventoryModal, setShowInventoryModal] = useState(false)
  const [showPreInstalledModal, setShowPreInstalledModal] = useState(false)
  const [showWarrantyModal, setShowWarrantyModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)
  const [showMissingModal, setShowMissingModal] = useState(false)
  const [showReturnFromMissingModal, setShowReturnFromMissingModal] = useState(false)
  const [editThermostatDevice, setEditThermostatDevice] = useState<ApiTypes.Model.Device | undefined>()
  const [updateIndex, setUpdateIndex] = useState(0)

  // Below statuses are prohibited to transfer to vendor
  const transferProhibitedAdditionalStatuses = userProfile?.contractor?.parent_company
    ? []
    : ["transferring", "preinstalled"]

  const toggleAllDevices = () => {
    if (isHeaderChecked) {
      const currentPageDeviceIds = devices.map(d => d.id)
      dispatch(setSelectedCustomerDevices(selectedCustomerDevices.filter(d => !currentPageDeviceIds.includes(d.id))))
    } else {
      const existingIds = new Set(selectedCustomerDevices.map(d => d.id))
      const toAdd = devices.filter(d => !existingIds.has(d.id))
      dispatch(setSelectedCustomerDevices([...selectedCustomerDevices, ...toAdd]))
    }
  }

  const onTransferClick = () => {
    setShowTransferModal(true)
  }

  const hasAnyMissingDevice = selectedCustomerDevices.some(device => device.phisical_status === "missing")
  const allDevicesAreMissing =
    selectedCustomerDevices.length > 0 && selectedCustomerDevices.every(device => device.phisical_status === "missing")

  const onMissingClick = () => {
    if (hasAnyMissingDevice) {
      setShowReturnFromMissingModal(true)
    } else {
      setShowMissingModal(true)
    }
  }

  const onSelectDevice = (id?: number) => {
    if (selectedCustomerDevices.some(item => item.id === id)) {
      dispatch(setSelectedCustomerDevices(selectedCustomerDevices.filter(item => item.id !== id)))
    } else {
      dispatch(setSelectedCustomerDevices([...selectedCustomerDevices, devices.find(item => item.id === id)!]))
    }
  }

  const handleInventoryClick = () => {
    setShowInventoryModal(true)
  }

  const handlePreInstalledClick = () => {
    setShowPreInstalledModal(true)
  }

  const handleWarrantyClick = () => {
    setShowWarrantyModal(true)
  }

  const handleCancel = () => {
    setShowInventoryModal(false)
    setShowPreInstalledModal(false)
    setShowWarrantyModal(false)
    setShowTransferModal(false)
    setShowMissingModal(false)
    setShowReturnFromMissingModal(false)
  }

  const handleSuccess = () => {
    setUpdateIndex(prev => prev + 1)
    handleCancel()
    onDevicesActionSuccess()
    dispatch(setSelectedCustomerDevices([]))
  }

  const handleShowEditThermostatModal = useCallback(
    (id: number) => {
      const device = devices.find(item => item.id === id)
      setEditThermostatDevice(device)
    },
    [devices],
  )

  const handleEditThermostatSuccess = () => {
    setEditThermostatDevice(undefined)
    onDevicesActionSuccess()
  }

  const getRowActions = useCallback(
    (rowData: UITypes.TableBodyRow) => {
      const status = rowData.phisical_status?.value as DevicePhysicalStatus | undefined
      const editableStatuses: DevicePhysicalStatus[] = [
        DevicePhysicalStatus.INVENTORY,
        DevicePhysicalStatus.PREINSTALLED,
        DevicePhysicalStatus.INSTALLED,
        DevicePhysicalStatus.MISSING,
        DevicePhysicalStatus.WARRANTY,
        DevicePhysicalStatus.DAMAGED,
        DevicePhysicalStatus.TRANSFERRING,
      ]
      const canEdit = !!status && editableStatuses.includes(status)
      return {
        actionButtons: [
          {
            icon: <IconEdit />,
            tooltipPosition: "top-start" as const,
            tooltip: "Edit thermostat",
            handler: handleShowEditThermostatModal,
            hidden: !canEdit,
          },
        ],
      }
    },
    [handleShowEditThermostatModal],
  )

  return (
    <>
      <div className={classNames(styles.widgetsWrapper, styles.widgetsWrapperJustified)}>
        <WidgetInventory updateIndex={updateIndex} />
        <WidgetMonitoring updateIndex={updateIndex} />
        <WidgetStatus updateIndex={updateIndex} />
      </div>
      <ContentWrapper title="Devices">
        <nav className={styles.tableNavWrapper}>
          <NavButtonsWrapper className={styles.tableNavButtonsWrapper}>
            <div className={styles.statusButtonWrapper}>
              <span
                className={classNames(styles.statusButton, styles.missing, {
                  [styles.disabled]:
                    !selectedCustomerDevices.length ||
                    (hasAnyMissingDevice
                      ? !allDevicesAreMissing
                      : selectedCustomerDevices.some(device =>
                          ["installed", "transferring", "preinstalled", "missing", "warranty"].includes(
                            device.phisical_status,
                          ),
                        )),
                })}
                onClick={onMissingClick}
              >
                {hasAnyMissingDevice ? "Return from Missing" : "Missing"}
              </span>
              <span
                className={classNames(styles.statusButton, styles.transfer, {
                  [styles.disabled]:
                    !selectedCustomerDevices.length ||
                    selectedCustomerDevices.some(device =>
                      ["damaged", ...transferProhibitedAdditionalStatuses].includes(device.phisical_status),
                    ),
                })}
                onClick={onTransferClick}
              >
                Transfer
              </span>
              <span
                className={classNames(styles.statusButton, styles.warranty, {
                  [styles.disabled]:
                    !selectedCustomerDevices.length ||
                    selectedCustomerDevices.some(
                      device => !["preinstalled", "installed", "inventory"].includes(device.phisical_status),
                    ),
                })}
                onClick={handleWarrantyClick}
              >
                Warranty
              </span>
              <span
                className={classNames(styles.statusButton, styles.inventory, {
                  [styles.disabled]:
                    selectedCustomerDevices.length === 0 ||
                    selectedCustomerDevices.some(device => device.phisical_status !== "preinstalled"),
                })}
                onClick={handleInventoryClick}
              >
                In Inventory
              </span>
              <span
                className={classNames(styles.statusButton, styles.preinstalled, {
                  [styles.disabled]:
                    selectedCustomerDevices.length === 0 ||
                    selectedCustomerDevices.some(device => device.phisical_status !== "inventory"),
                })}
                onClick={handlePreInstalledClick}
              >
                Pre-Installed
              </span>
            </div>
            <RightSideButtons>
              <EditColumnsButton tableName="contractor_devices_list" tableHeaderCells={tableHeaderCells} />
              <ButtonNew onClick={onExport} className={classNames(styles.tableNavItem)} disabled={meta?.total === 0}>
                Export
              </ButtonNew>
            </RightSideButtons>
          </NavButtonsWrapper>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          data={convertDevicesToContractorDevicesTableRows(devices)}
          isLoading={isFetching}
          columnsLoaded={columnsLoaded}
          onCheckboxChange={onSelectDevice}
          onHeaderCheckboxChange={toggleAllDevices}
          currentItemIDs={selectedCustomerDevices.map(item => item.id)}
          isHeaderChecked={isHeaderChecked}
          additionalTags={
            selectedCustomerDevices?.length
              ? [
                  {
                    label: `Selected Device${selectedCustomerDevices.length > 1 ? "s" : ""} S/N`,
                    values: selectedCustomerDevices?.map(item => item.sn),
                  },
                ]
              : []
          }
          clearAdditionalTags={() => dispatch(setSelectedCustomerDevices([]))}
          getRowActions={getRowActions}
        />
        <Pagination meta={meta} />
      </ContentWrapper>
      {showInventoryModal && (
        <InventoryConfirmationModal
          onCancel={handleCancel}
          onSuccess={handleSuccess}
          selectedDevices={selectedCustomerDevices}
        />
      )}
      {showPreInstalledModal && (
        <PreinstalledConfirmationModal
          onCancel={handleCancel}
          onSuccess={handleSuccess}
          selectedDevices={selectedCustomerDevices}
        />
      )}
      {showWarrantyModal && (
        <WarrantyConfirmationModal
          onCancel={handleCancel}
          onSuccess={handleSuccess}
          selectedDevices={selectedCustomerDevices}
        />
      )}
      {showTransferModal && (
        <TransferModal
          onCancel={handleCancel}
          onSuccess={handleSuccess}
          skipVendor={
            !!userProfile?.contractor?.parent_company &&
            selectedCustomerDevices.some(device => ["transferring", "preinstalled"].includes(device.phisical_status))
          }
        />
      )}
      {showMissingModal && <MissingModalContractor onCancel={handleCancel} onSuccess={handleSuccess} />}
      {showReturnFromMissingModal && (
        <ReturnFromMissingModalContractor onCancel={handleCancel} onSuccess={handleSuccess} />
      )}
      {editThermostatDevice && (
        <EditThermostatModal
          device={editThermostatDevice}
          onClose={() => setEditThermostatDevice(undefined)}
          onSuccess={handleEditThermostatSuccess}
        />
      )}
    </>
  )
}

export default ContractorDevicesPageContent
